import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const PAGE_SIZE_OPTIONS = [50, 100, 200];
const DEFAULT_PAGE_SIZE = 50;

const sanitize = (v) => (PAGE_SIZE_OPTIONS.includes(Number(v)) ? Number(v) : DEFAULT_PAGE_SIZE);

/**
 * usePagination — applied to ANY filtered array.
 * @param {Array} items - the already-filtered list to paginate
 * @param {string} storageKey - localStorage key to persist the page size
 * @returns { pageItems, page, setPage, pageSize, setPageSize, totalPages, totalItems }
 */
export function usePagination(items, storageKey = "voltledger.pageSize") {
  const [pageSize, setPageSizeState] = useState(() => {
    try {
      return sanitize(localStorage.getItem(storageKey));
    } catch (_e) {
      return DEFAULT_PAGE_SIZE;
    }
  });
  const [page, setPage] = useState(1);

  const setPageSize = (n) => {
    const next = sanitize(n);
    setPageSizeState(next);
    setPage(1);
    try {
      localStorage.setItem(storageKey, String(next));
    } catch (_e) {
      // ignore quota errors
    }
  };

  const totalItems = items?.length || 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // Snap back if filters shrink the list past the current page
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageItems = useMemo(() => {
    if (!Array.isArray(items)) return [];
    const start = (page - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, page, pageSize]);

  return { pageItems, page, setPage, pageSize, setPageSize, totalPages, totalItems };
}

/**
 * Pagination — renders the size picker + page navigator.
 * Render this directly after your table when totalItems > 0.
 */
export default function Pagination({
  page, totalPages, totalItems, pageSize,
  setPage, setPageSize, testId = "pagination",
}) {
  if (!totalItems) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);
  return (
    <div
      className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 bg-slate-50/60 text-xs flex-wrap"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-slate-600">
        <span>Showing</span>
        <Select value={String(pageSize)} onValueChange={setPageSize}>
          <SelectTrigger className="h-8 w-20 rounded-none border-slate-300 text-xs" data-testid={`${testId}-size`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((opt) => (
              <SelectItem key={opt} value={String(opt)}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span>
          per page · <strong className="text-slate-900">{start.toLocaleString("en-IN")}–{end.toLocaleString("en-IN")}</strong> of {totalItems.toLocaleString("en-IN")}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed rounded-none"
          data-testid={`${testId}-prev`}
          aria-label="Previous page"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Prev
        </button>
        <span className="px-3 font-mono-data text-slate-700" data-testid={`${testId}-current`}>
          Page <strong>{page}</strong> / {totalPages}
        </span>
        <button
          onClick={() => setPage(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed rounded-none"
          data-testid={`${testId}-next`}
          aria-label="Next page"
        >
          Next <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
